import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import BoundImageModal from 'src/components/boundImageModal';
import {StyledPageCenterContainer, pxToEm} from 'src/components/globalStyles';
import HexagonIconImage from 'src/components/hexagonIconImage';
import Layout from 'src/components/layout';
import PageViewHeadline from 'src/components/pageViewHeadline';
import WrapperAppUl from 'src/components/wrapperAppUl';
import {Product, ProductToHexagonIconSrc} from 'src/data/product';
import PlaybackCategoryDiagram from 'src/media/playbackPage/playback-category-cycle.png';
import PlaybackWorkflowDiagram from 'src/media/playbackPage/playback-workflow.png';
import styled from 'styled-components';

import PlaybackFeaturesList from './featuresList';

//#region Styled Components

const StyledH2 = styled.h2`
  font-size: ${pxToEm(24)}em;
  color: ${baseVariables.colors.midnight};
  display: inline-block;
  margin-bottom: ${pxToEm(40) / pxToEm(24)}em;
`;

const StyledContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 50%;
`;

const StyledTwoColumnsSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;

  > ${StyledContentColumn} {
    flex: 1 1 50%;

    :nth-child(1) {
      padding-right: 5%;
    }

    :nth-child(2) {
      border-left: 2px solid ${baseVariables.colors.darkBlueGray};
      padding-left: 5%;
    }
  }
`;

const StyledFeaturesSection = styled(PlaybackFeaturesList)`
  margin-top: ${pxToEm(78)}em;
`;

const StyledImageModal = styled(BoundImageModal)`
  margin: 1em 1em;
  margin-top: ${pxToEm(78)}em;
`;

const StyledImg = styled.img`
  width: 100%;
`;

const StyledListWrapperDiv = styled.div`
  width: 100%;
  padding: 0 ${pxToEm(20)}em;
  margin-bottom: ${pxToEm(30)}em;
`;

//#endregion

class PlaybackPageView extends React.Component {
  public render() {
    const playbackCategoryDiagram = <StyledImg src={PlaybackCategoryDiagram} alt="Playback Diagram" />;
    const playbackWorkflowDiagram = <StyledImg src={PlaybackWorkflowDiagram} alt="Playback Workflow Diagram" />;
    return (
      <Layout>
        <PageViewHeadline
          topSubHeadline={'Playback'}
          headline={'Deploy, test & optimize to provide flawless video playback'}
          badgeComponent={<HexagonIconImage iconSrc={ProductToHexagonIconSrc[Product.Player]} />}
        />

        <StyledPageCenterContainer>
          <StyledTwoColumnsSection>
            <StyledContentColumn>
              <StyledImageModal boundComponent={playbackCategoryDiagram} source={PlaybackCategoryDiagram} />
            </StyledContentColumn>
            <StyledContentColumn>
              <StyledFeaturesSection />
            </StyledContentColumn>
          </StyledTwoColumnsSection>
          <StyledListWrapperDiv>
            <StyledImageModal boundComponent={playbackWorkflowDiagram} source={PlaybackWorkflowDiagram} />
            <StyledH2>Deliver Flawless Playback</StyledH2>
            <WrapperAppUl>
              <li>
                Bitmovin's Playback solution allows development teams to easily add video playback on Mobile, Web, Games
                Consoles and Smart TV applications with performance analytics &amp; automated testing to ensure you
                deliver the highest quality of experience to audiences
              </li>
              <li>
                Bitmovin Playback is built for developers working across the complete playback lifecycle on all devices.
                Giving a fast, scalable and efficient way to test, deploy, play, debug and optimize
              </li>
            </WrapperAppUl>
          </StyledListWrapperDiv>
          <StyledListWrapperDiv>
            <StyledH2>Quality, Guaranteed</StyledH2>
            <WrapperAppUl>
              <li>Test your streams on physical devices, using Stream Lab</li>
              <li>
                Configure Player with optimal ABR settings, UI design, DRM, Ads, Captions and more to deliver a flawless
                viewer experience
              </li>
              <li>Measure, debug and optimize video playback using Analytics</li>
              <li>
                The fast feedback loop between testing, deploying and optimizing allows developers to elevate the
                experience they offer viewers and provides them with more time to work on new feature development
              </li>
            </WrapperAppUl>
          </StyledListWrapperDiv>
          <StyledListWrapperDiv>
            <StyledH2>Operational Efficiency</StyledH2>
            <WrapperAppUl>
              <li>
                Bitmovin Playback is easy to use and gives developers the tools they need through a single dashboard
                portal
              </li>
              <li>
                The embeddable video player is pre-tested and already optimized for all major devices. Integrated
                wizards make deployments fast and easy on all devices
              </li>
              <li>
                Built-in Analytics and Stream Lab give developers clear and actionable insights into how playback is
                performing for viewers
              </li>
              <li>
                When integrating the Bitmovin Player, gain access to our complete API library for a unified developer
                experience across devices, and reduce your time-to-market and total cost of ownership by more than 25%
              </li>
            </WrapperAppUl>
          </StyledListWrapperDiv>
        </StyledPageCenterContainer>
      </Layout>
    );
  }
}

export default PlaybackPageView;
